import React, { useEffect, useRef } from "react";
import { Conversation } from "../../../types/Entities/Messaging/Conversation";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import MessageCenterMessage from "./MessageCenterMessage";
import { ArrowBackOutlined } from "@mui/icons-material";
import { markMessagesAsRead } from "../../../services/messagingService";
import { queryKeys } from "../../../services/reactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { Message } from "../../../types/Entities/Messaging/Message";
import { MinimalCase } from "../../../types/Entities/MinimalCase";
import Typography from "@mui/material/Typography";
import ProductBox from "@/src/components/ProductBox/ProductBox";

type MessageCenterConversationProps = {
  conversation: Conversation;
  setOpenConversationId: (conversationId: number | null) => void;
  product: MinimalCase;
};

export default function MessageCenterConversation({ conversation, setOpenConversationId, product }: MessageCenterConversationProps) {
  const resetConversation = () => setOpenConversationId(null);
  const queryClient = useQueryClient();
  const conversationRef = useRef<null | HTMLDivElement>(null!);

  const sortComparisonFunction = (MessageA: Message, MessageB: Message) => {
    if (MessageA.created < MessageB.created) {
      return -1;
    }
    if (MessageA.created > MessageB.created) {
      return 1;
    }
    return 0;
  };

  const sortedMessages = conversation.containedMessages.sort(sortComparisonFunction);

  useEffect(() => {
    markMessagesAsRead(conversation.containedMessages).then(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.collection("/conversations"),
      });
    });

    if (conversation.containedMessages.length > 1) {
      conversationRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <>
      <Box ref={conversationRef} />
      <ProductBox variant={"divider"} headline={conversation.subject}>
        <Button onClick={resetConversation} startIcon={<ArrowBackOutlined />} sx={{ paddingLeft: "0.5rem" }}>
          Zurück
        </Button>
        <Box>
          {sortedMessages.map((message, index) => (
            <React.Fragment key={message.id}>
              {index === sortedMessages.length - 1 && <Box ref={conversationRef} />}
              <MessageCenterMessage message={message} product={product} />
            </React.Fragment>
          ))}
          {conversation.isClosed && (
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                margin: "2rem 0",
              }}
            >
              Diese Unterhaltung wurde beendet. Sie können nicht mehr darauf antworten.
            </Typography>
          )}
        </Box>
      </ProductBox>
    </>
  );
}
